export interface ForgeArchetype {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  badge: string;
  sampleImageUrl: string;
  defaultSubject: string;
  smartPresetIds: string[]; // Preset IDs from StudioPresetsData to pre-select
  smartRecipeModifier: string; // Auto-injected smart rules (e.g. no phone in selfie)
  recommendedModel: string;
}

export const FORGE_ARCHETYPES: ForgeArchetype[] = [
  {
    id: 'selfie',
    name: 'Realistic Selfie',
    subtitle: 'Natural & candid',
    description: 'Creates hyper-realistic smartphone photos. Injects smart negative rules so the camera phone is kept off-screen!',
    badge: '📱 Social POV',
    sampleImageUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=800&q=80',
    defaultSubject: 'A natural candid smartphone selfie of a young woman at an outdoor sunset cafe, looking directly into the camera lens with a soft genuine smile',
    smartPresetIds: ['85mm_portrait', 'golden_hour'],
    smartRecipeModifier: 'iPhone front-camera POV selfie, candid unposed moment, subject looking directly into camera lens, natural ambient lighting, phone held off-screen, no phone in frame',
    recommendedModel: 'Google Imagen 3',
  },
  {
    id: 'cinematic_portrait',
    name: 'Cinematic Portrait',
    subtitle: 'Dramatic lighting',
    description: 'Professional portraiture with 85mm prime lens optics, shallow depth of field, and studio softbox illumination.',
    badge: '📸 Dramatic Studio',
    sampleImageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&q=80',
    defaultSubject: 'A striking cinematic portrait of a dignified man wearing a dark tailored suit and fedora, subtle rim light highlighting his profile',
    smartPresetIds: ['85mm_portrait', 'softbox_studio', 'ecu'],
    smartRecipeModifier: 'cinematic portrait shot on 85mm f/1.4 prime lens, Rembrandt softbox studio lighting, creamy background bokeh, sharp eye focus',
    recommendedModel: 'Google Imagen 3',
  },
  {
    id: 'vintage_film',
    name: 'Vintage Film',
    subtitle: 'Analog grain & color',
    description: 'Nostalgic analog film aesthetic with organic grain, warm amber tones, and European seaside warmth.',
    badge: '🎞️ 35mm Analog',
    sampleImageUrl: 'https://images.unsplash.com/photo-1516483638261-f4dbaf036963?auto=format&fit=crop&w=800&q=80',
    defaultSubject: 'A nostalgic scene at a Mediterranean seaside cafe terrace overlooking the coast during golden sunset hour',
    smartPresetIds: ['35mm_anamorphic', 'golden_hour'],
    smartRecipeModifier: 'retro 35mm film photography, Kodak Portra 400 color science, subtle analog film grain, warm nostalgic amber tones, 1970s aesthetic',
    recommendedModel: 'Midjourney v6.1',
  },
  {
    id: '3d_animation',
    name: '3D Animation',
    subtitle: 'Stylized characters',
    description: 'Vibrant animated movie character style with glossy Octane 3D textures and charming facial expressions.',
    badge: '🎨 3D Animation',
    sampleImageUrl: 'https://images.unsplash.com/photo-1563089145-599997674d42?auto=format&fit=crop&w=800&q=80',
    defaultSubject: 'A charming 3D animated character portrait of an expressive young girl with wide curious eyes in a Pixar animation style',
    smartPresetIds: ['softbox_studio'],
    smartRecipeModifier: '3D animated movie character portrait, Pixar and DreamWorks style, vibrant expressive eyes, Octane 3D render, smooth stylized textures',
    recommendedModel: 'ChatGPT Image',
  },
  {
    id: 'cyberpunk',
    name: 'Sci-Fi / Cyberpunk',
    subtitle: 'Futuristic worlds',
    description: 'Moody futuristic cityscapes with vivid dual cyan/magenta neon reflections, volumetric mist, and dark shadows.',
    badge: '🌆 Sci-Fi Neon',
    sampleImageUrl: 'https://images.unsplash.com/photo-1514565131-fce0801e5785?auto=format&fit=crop&w=800&q=80',
    defaultSubject: 'A cybernetic metropolis night street with glowing holographic neon billboards, flying vehicles, and rain reflections',
    smartPresetIds: ['35mm_anamorphic', 'cyberpunk_neon', 'god_rays'],
    smartRecipeModifier: 'cinematic cyberpunk scene, rain-slicked city streets with glowing neon reflections, volumetric haze, dual cyan and magenta backlight',
    recommendedModel: 'Midjourney v6.1',
  },
  {
    id: 'product',
    name: 'Product Photo',
    subtitle: 'Commercial & clean',
    description: 'Clean commercial food and product photography with crisp macro textures, rim lighting, and flawless backgrounds.',
    badge: '💎 Commercial',
    sampleImageUrl: 'https://images.unsplash.com/photo-1541701494587-cb58502866ab?auto=format&fit=crop&w=800&q=80',
    defaultSubject: 'A luxurious glass bottle of artisanal perfume resting on a reflective warm surface with soft studio backlighting and water droplets',
    smartPresetIds: ['macro', 'softbox_studio'],
    smartRecipeModifier: 'commercial product photography, crisp macro detail, studio rim light, soft fill light, high resolution advertising aesthetic',
    recommendedModel: 'Google Imagen 3',
  },
  {
    id: 'fine_art',
    name: 'Fine Art',
    subtitle: 'Painterly',
    description: 'Classic painterly composition inspired by oil on canvas, impressionism, rich brushwork, and atmospheric depth.',
    badge: '🖌️ Painterly',
    sampleImageUrl: 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?auto=format&fit=crop&w=800&q=80',
    defaultSubject: 'A serene mountain lake framed by evergreen pine trees in an oil painting style with thick expressive brushstrokes',
    smartPresetIds: ['golden_hour'],
    smartRecipeModifier: 'fine art oil painting, expressive impasto brushwork, rich color palette, classical museum gallery aesthetic, textured canvas',
    recommendedModel: 'Recraft v3',
  },
  {
    id: 'custom',
    name: 'Custom / Blank Canvas',
    subtitle: 'Full manual control',
    description: 'Start with a clean slate and build your prompt entirely from scratch using the full cinematography control deck.',
    badge: '✍️ Freeform',
    sampleImageUrl: 'https://images.unsplash.com/photo-1508614589041-895b88991e3e?auto=format&fit=crop&w=800&q=80',
    defaultSubject: '',
    smartPresetIds: [],
    smartRecipeModifier: '',
    recommendedModel: 'Google Imagen 3',
  },
];
